import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import type { QuerygenLegacyTablePayload } from "../types";

type QueryResultTableProps = {
  payload: QuerygenLegacyTablePayload;
};

type InferredDataType = "numeric" | "text" | "date" | "boolean" | "json";

const formatCellValue = (value: unknown): string => {
  if (value == null) {
    return "\u2014";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return String(value);
};

const inferDataType = (
  columnIndex: number,
  rows: unknown[][],
): InferredDataType => {
  for (const row of rows.slice(0, 20)) {
    const value = row[columnIndex];
    if (value == null) {
      continue;
    }
    if (typeof value === "boolean") {
      return "boolean";
    }
    if (typeof value === "number") {
      return "numeric";
    }
    if (typeof value === "object") {
      return "json";
    }
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      return "date";
    }
    if (/^-?\d+(\.\d+)?$/.test(str.trim())) {
      return "numeric";
    }
  }
  return "text";
};

const dataTypeBadge: Record<InferredDataType, { label: string; className: string }> = {
  numeric: {
    label: "num",
    className: "bg-blue-100 text-blue-800",
  },
  text: {
    label: "text",
    className: "bg-green-100 text-green-800",
  },
  date: {
    label: "date",
    className: "bg-purple-100 text-purple-800",
  },
  boolean: {
    label: "bool",
    className: "bg-orange-100 text-orange-800",
  },
  json: {
    label: "json",
    className: "bg-slate-100 text-slate-800",
  },
};

const getColumnMinWidth = (
  columnIndex: number,
  header: string,
  rows: unknown[][],
): string => {
  let maxLen = header.length;
  for (const row of rows.slice(0, 30)) {
    const len = formatCellValue(row[columnIndex]).length;
    if (len > maxLen) {
      maxLen = len;
    }
  }
  if (maxLen <= 6) return "min-w-20";
  if (maxLen <= 10) return "min-w-24";
  if (maxLen <= 16) return "min-w-32";
  if (maxLen <= 24) return "min-w-40";
  if (maxLen <= 32) return "min-w-48";
  if (maxLen <= 48) return "min-w-64";
  return "min-w-80";
};

export function QueryResultTable({ payload }: QueryResultTableProps) {
  const rowCount = payload.pagination?.totalRows ?? payload.rows.length;

  const columnTypes = useMemo(
    () => payload.headers.map((_, index) => inferDataType(index, payload.rows)),
    [payload],
  );

  return (
    <div className="mt-4 w-full">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="querygen-mono text-[10px] uppercase tracking-[0.18em] text-[#9ca3af]">
          Query Results
        </div>
        <div className="text-[12px] text-[#6b7280]">
          {rowCount.toLocaleString()} row
          {rowCount === 1 ? "" : "s"}
          {payload.pagination ? (
            <>
              {" \u00b7 "}Page {payload.pagination.currentPage} of{" "}
              {payload.pagination.totalPages}
            </>
          ) : null}
        </div>
      </div>

      {!payload.rows.length ? (
        <div className="rounded-md border border-dashed border-slate-200/90 bg-slate-50/60 px-5 py-6 text-[13px] text-slate-600">
          Query returned no rows.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-slate-200/70 bg-white shadow-sm">
          <div className="max-h-[480px] overflow-auto overscroll-x-contain">
            <Table className="min-w-max table-auto border-collapse">
              <TableHeader>
                <TableRow className="border-b border-slate-200/80 bg-slate-100 hover:bg-slate-100">
                  {payload.headers.map((column, columnIndex) => {
                    const badge = dataTypeBadge[columnTypes[columnIndex]];
                    const minW = getColumnMinWidth(
                      columnIndex,
                      column,
                      payload.rows,
                    );
                    return (
                      <TableHead
                        key={column}
                        className={`sticky top-0 z-10 h-auto whitespace-nowrap border-r border-slate-200 bg-slate-100 px-4 py-3 text-left text-[12px] font-semibold text-slate-700 last:border-r-0 ${minW}`}
                      >
                        <div className="flex items-center gap-2">
                          <span>{column}</span>
                          <span
                            className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wider ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {payload.rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex} className="border-slate-200 last:border-b-0">
                    {payload.headers.map((column, columnIndex) => (
                      <TableCell
                        key={`${rowIndex}-${column}`}
                        className="max-w-[340px] border-r border-slate-200 px-4 py-2.5 align-top text-[13px] leading-6 text-slate-800 last:border-r-0"
                      >
                        <div className="overflow-hidden whitespace-pre-wrap break-words">
                          {formatCellValue(row[columnIndex])}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
